import { environmentSchema, platformSchema } from '@cx-sdk/types';
import { z } from 'zod';

/**
 * Shared refinement message for the "apiHost XOR (environment + platform)" rule.
 */
export const API_HOST_XOR_ENV_PLATFORM_MESSAGE =
  'Must provide either apiHost OR both environment and platform, but not both';

/**
 * Wraps a base params schema in the shared "apiHost XOR (environment + platform)"
 * union used by every HTTP SDK constructor: either an explicit `apiHost` is given,
 * or both `environment` and `platform` are, but never both combinations.
 *
 * Callers apply their own `.refine(...)` for any additional invariants (and, for
 * the apiHost/env rule itself, {@link hasApiHostXorEnvPlatform}).
 */
export function withApiHostOrEnvPlatform<T extends z.ZodRawShape>(baseSchema: z.ZodObject<T>) {
  return z.union([
    baseSchema.extend({
      apiHost: z.string(),
      environment: z.undefined(),
      platform: z.undefined(),
    }),
    baseSchema.extend({
      apiHost: z.undefined(),
      environment: environmentSchema,
      platform: platformSchema,
    }),
  ]);
}

/**
 * Refinement predicate for the apiHost XOR env+platform invariant.
 */
export function hasApiHostXorEnvPlatform(data: {
  apiHost?: string;
  environment?: unknown;
  platform?: unknown;
}): boolean {
  return (
    (!!data.apiHost && !data.environment && !data.platform) || (!data.apiHost && !!data.environment && !!data.platform)
  );
}
